/**
 * Security Framework Reducer
 * 
 * Manages state for the Security Framework component including:
 * - Framework definitions (predefined & custom)
 * - Selected rules and their associations
 * - Business values and dreaded events mappings
 * - Framework migration logic for language switching
 * - CSV import state
 * 
 * @module SecurityFrameworkReducer
 */

// Action Types
export const ACTION_TYPES = {
    // Loading
    LOAD_START: 'LOAD_START',
    LOAD_DEFINITIONS_SUCCESS: 'LOAD_DEFINITIONS_SUCCESS',
    LOAD_SELECTIONS_SUCCESS: 'LOAD_SELECTIONS_SUCCESS',
    LOAD_ERROR: 'LOAD_ERROR',

    // Framework Management
    ADD_CUSTOM_FRAMEWORK: 'ADD_CUSTOM_FRAMEWORK',
    UPDATE_SAVED_DEFINITIONS: 'UPDATE_SAVED_DEFINITIONS',
    SELECT_FRAMEWORK: 'SELECT_FRAMEWORK',

    // Rule Management
    TOGGLE_RULE: 'TOGGLE_RULE',
    UPDATE_RULE: 'UPDATE_RULE',
    UPDATE_RULE_BVS: 'UPDATE_RULE_BVS',
    UPDATE_RULE_DES: 'UPDATE_RULE_DES',
    UPDATE_RULE_GAP_COMMENT: 'UPDATE_RULE_GAP_COMMENT',

    // Search & Filter
    SET_SEARCH_TERM: 'SET_SEARCH_TERM',

    // Saving
    SAVE_START: 'SAVE_START',
    SAVE_SUCCESS: 'SAVE_SUCCESS',
    SAVE_ERROR: 'SAVE_ERROR',

    // CSV Import
    SET_CSV_DATA: 'SET_CSV_DATA',
    SHOW_IMPORT_MODAL: 'SHOW_IMPORT_MODAL',
    HIDE_IMPORT_MODAL: 'HIDE_IMPORT_MODAL',
    SET_IMPORT_ERROR: 'SET_IMPORT_ERROR',

    // Framework Names Cache
    UPDATE_FRAMEWORK_NAMES_CACHE: 'UPDATE_FRAMEWORK_NAMES_CACHE',
    APPLY_MIGRATION: 'APPLY_MIGRATION',

    // Custom Framework Management
    DELETE_CUSTOM_FRAMEWORK: 'DELETE_CUSTOM_FRAMEWORK',
    RENAME_CUSTOM_FRAMEWORK: 'RENAME_CUSTOM_FRAMEWORK'
};

// Initial State
export const initialState = {
    frameworkDefinitions: [],      // Will hold both predefined & custom from API + newly imported
    selectedRules: {},              // { frameworkId: [rules] }
    ruleBusinessValues: {},         // { ruleId: [businessValueIds] }
    ruleDreadedEvents: {},          // { ruleId: [dreadedEventIds] }
    ruleGapComments: {},            // { ruleId: string } — per-rule gap comment
    frameworkNamesCache: {},        // { frameworkId: frameworkName } - Preserves original framework names
    isLoadingDefinitions: true,
    isSaving: false,
    error: null,                    // Combined load/save error state
    loadError: null,                // Specific loading error
    saveError: null,                // Specific saving error
    selectedFrameworkId: null,
    searchTerm: '',
    csvImportData: null,
    showImportModal: false,
    importError: null
};

/**
 * Security Framework Reducer
 * @param {Object} state - Current state
 * @param {Object} action - Action object with type and payload
 * @returns {Object} - New state
 */
export function securityFrameworkReducer(state, action) {
    switch (action.type) {
        case ACTION_TYPES.LOAD_START:
            // Reset errors on load start
            return { ...state, isLoadingDefinitions: true, loadError: null, error: null };

        case ACTION_TYPES.LOAD_DEFINITIONS_SUCCESS: {
            const definitions = action.payload.definitions || [];

            let migratedSelectedRules = { ...state.selectedRules };
            let migratedRuleBusinessValues = { ...state.ruleBusinessValues };
            let migratedRuleDreadedEvents = { ...state.ruleDreadedEvents };
            let updatedFrameworkNamesCache = { ...state.frameworkNamesCache };
            let newSelectedFrameworkId = state.selectedFrameworkId;

            const frameworkMigrationMap = new Map();

            // Lazy migration: only exact matches, no fuzzy
            Object.keys(state.selectedRules).forEach(oldKey => {
                let matchingFramework = definitions.find(fw => fw.stableId === oldKey);
                if (!matchingFramework) {
                    matchingFramework = definitions.find(fw => (fw._id === oldKey || fw.id === oldKey));
                }
                if (!matchingFramework) {
                    const cachedName = state.frameworkNamesCache[oldKey];
                    const isCustom = oldKey.startsWith('custom-') || (cachedName && !['cis', 'nist', 'iso', 'anssi', 'hygiène'].some(k => cachedName.toLowerCase().includes(k)));
                    if (isCustom) {
                        // custom: exact name only, no fuzzy
                        if (cachedName) {
                            matchingFramework = definitions.find(fw => fw.name === cachedName);
                        }
                    } else if (cachedName) {
                        // predefined: type from cachedName, then exact name
                        const lower = cachedName.toLowerCase();
                        let type = null;
                        if (lower.includes('cis')) type = 'cis';
                        else if (lower.includes('nist')) type = 'nist';
                        else if (lower.includes('iso')) type = 'iso27002';
                        else if (lower.includes('anssi') || lower.includes('hygiène')) type = 'anssi';
                        if (type) {
                            matchingFramework = definitions.find(fw => fw.type === type);
                            if (!matchingFramework) {
                                matchingFramework = definitions.find(fw => fw.name === cachedName);
                            }
                        } else {
                            matchingFramework = definitions.find(fw => fw.name === cachedName);
                        }
                    }
                }
                if (matchingFramework) {
                    const newKey = matchingFramework.stableId || matchingFramework._id || matchingFramework.id;
                    if (newKey !== oldKey) {
                        frameworkMigrationMap.set(oldKey, newKey);
                    }
                }
            });

            if (frameworkMigrationMap.size > 0) {
                migratedSelectedRules = {};
                Object.entries(state.selectedRules).forEach(([oldKey, rules]) => {
                    const newKey = frameworkMigrationMap.get(oldKey) || oldKey;
                    migratedSelectedRules[newKey] = rules;
                });
                const newFrameworkNamesCache = {};
                Object.entries(state.frameworkNamesCache).forEach(([oldKey, name]) => {
                    const newKey = frameworkMigrationMap.get(oldKey) || oldKey;
                    newFrameworkNamesCache[newKey] = name;
                });
                updatedFrameworkNamesCache = newFrameworkNamesCache;
                if (frameworkMigrationMap.has(state.selectedFrameworkId)) {
                    newSelectedFrameworkId = frameworkMigrationMap.get(state.selectedFrameworkId);
                }
            }

            Object.keys(migratedSelectedRules).forEach(frameworkKey => {
                const framework = definitions.find(f => (f.stableId || f._id || f.id) === frameworkKey);
                if (framework) {
                    updatedFrameworkNamesCache[frameworkKey] = framework.name;
                }
            });

            const currentSelectedIsValid = definitions.some(f => (f.stableId || f.id || f._id) === newSelectedFrameworkId);

            return {
                ...state,
                isLoadingDefinitions: false,
                frameworkDefinitions: definitions,
                selectedRules: migratedSelectedRules,
                ruleBusinessValues: migratedRuleBusinessValues,
                ruleDreadedEvents: migratedRuleDreadedEvents,
                frameworkNamesCache: updatedFrameworkNamesCache,
                selectedFrameworkId: currentSelectedIsValid ? newSelectedFrameworkId : null,
                loadError: null,
                error: null
            };
        }

        case ACTION_TYPES.LOAD_SELECTIONS_SUCCESS: {
            const loadedSelectedRules = action.payload.savedData.selectedRules || {};
            const loadedFrameworkNamesCache = action.payload.savedData.frameworkNamesCache || {};

            let updatedCache = { ...loadedFrameworkNamesCache };
            Object.keys(loadedSelectedRules).forEach(frameworkKey => {
                if (!updatedCache[frameworkKey]) {
                    const framework = state.frameworkDefinitions.find(f => (f.stableId || f._id || f.id) === frameworkKey);
                    if (framework) {
                        updatedCache[frameworkKey] = framework.name;
                    }
                }
            });

            return {
                ...state,
                selectedRules: loadedSelectedRules,
                ruleBusinessValues: action.payload.savedData.ruleBusinessValues || {},
                ruleDreadedEvents: action.payload.savedData.ruleDreadedEvents || {},
                ruleGapComments: action.payload.savedData.ruleGapComments || {},
                frameworkNamesCache: updatedCache,
            };
        }

        case ACTION_TYPES.LOAD_ERROR:
            return { ...state, isLoadingDefinitions: false, loadError: action.payload, error: action.payload };

        case ACTION_TYPES.ADD_CUSTOM_FRAMEWORK: {
            const { framework } = action.payload;
            // Add the is_new flag to track unsaved definitions
            const newFramework = { ...framework, is_new: true };
            const updatedDefinitions = [...state.frameworkDefinitions, newFramework];
            return {
                ...state,
                frameworkDefinitions: updatedDefinitions,
                selectedFrameworkId: newFramework.id, // Auto-select the new framework
                showImportModal: false, // Close modal implicitly on add
                csvImportData: null,
                importError: null
            };
        }

        case ACTION_TYPES.UPDATE_SAVED_DEFINITIONS:
            // Action to update definitions after saving
            return {
                ...state,
                frameworkDefinitions: action.payload.updatedDefinitions,
            };

        case ACTION_TYPES.SELECT_FRAMEWORK:
            // Prevent unnecessary state updates if selecting the same framework
            if (state.selectedFrameworkId === action.payload) {
                console.log(`[SELECT_FRAMEWORK] Framework ${action.payload} already selected, no state change`);
                return state;
            }
            console.log(`[SELECT_FRAMEWORK] Changing from ${state.selectedFrameworkId} to ${action.payload}`);
            return { ...state, selectedFrameworkId: action.payload, searchTerm: '' };

        case ACTION_TYPES.TOGGLE_RULE: {
            const { frameworkId, rule } = action.payload;
            const currentRules = state.selectedRules[frameworkId] || [];
            
            // Try matching by ID first
            let ruleIndex = currentRules.findIndex(r => r.id === rule.id);
            
            // If not found by ID, try matching by name (cross-language support)
            if (ruleIndex === -1 && rule.name) {
                const normalizedName = rule.name.trim().toLowerCase();
                ruleIndex = currentRules.findIndex(r => r.name && r.name.trim().toLowerCase() === normalizedName);
            }
            
            let updatedRules;
            let updatedBVs = { ...state.ruleBusinessValues };
            let updatedDEs = { ...state.ruleDreadedEvents };
            let updatedFrameworkNamesCache = { ...state.frameworkNamesCache };

            if (ruleIndex > -1) { // Rule exists, remove it and its associations
                const removedRule = currentRules[ruleIndex];
                updatedRules = currentRules.filter((_, idx) => idx !== ruleIndex);
                delete updatedBVs[removedRule.id];
                delete updatedDEs[removedRule.id];
            } else { // Rule doesn't exist, add it
                const newRule = {
                    id: rule.id,
                    name: rule.name,
                    description: rule.description || '',
                    status: 1, // Non applicable (default)
                    justification: ''
                };
                updatedRules = [...currentRules, newRule];

                // ponytail: auto-link to virtual ER so PACS composite keys stay alive
                updatedDEs[rule.id] = ['__v'];

                // Cache the framework name when first rule is added
                if (!updatedFrameworkNamesCache[frameworkId]) {
                    const framework = state.frameworkDefinitions.find(f => (f._id || f.id) === frameworkId);
                    if (framework) {
                        updatedFrameworkNamesCache[frameworkId] = framework.name;
                    }
                }
            }

            const newSelectedRules = { ...state.selectedRules };
            if (updatedRules.length > 0) {
                newSelectedRules[frameworkId] = updatedRules;
            } else { // If no rules left for this framework, remove the framework key
                delete newSelectedRules[frameworkId];
            }
            return {
                ...state,
                selectedRules: newSelectedRules,
                ruleBusinessValues: updatedBVs,
                ruleDreadedEvents: updatedDEs,
                frameworkNamesCache: updatedFrameworkNamesCache
            };
        }

        case ACTION_TYPES.UPDATE_RULE: {
            const { frameworkId, ruleId, field, value } = action.payload;
            const currentRules = state.selectedRules[frameworkId] || [];
            const updatedRules = currentRules.map(r => r.id === ruleId ? { ...r, [field]: value } : r);
            // Ensure the frameworkId exists before trying to update
            if (!state.selectedRules[frameworkId]) {
                console.warn(`Attempted to update rule for non-existent framework selection: ${frameworkId}`);
                return state; // No change if framework isn't selected/doesn't have rules yet
            }
            return { ...state, selectedRules: { ...state.selectedRules, [frameworkId]: updatedRules } };
        }

        case ACTION_TYPES.UPDATE_RULE_BVS: {
            const { ruleId, businessValueIds } = action.payload;
            const newRuleBVs = { ...state.ruleBusinessValues };
            if (businessValueIds && businessValueIds.length > 0) {
                newRuleBVs[ruleId] = businessValueIds;
            } else { // Remove the key if the array is empty or null
                delete newRuleBVs[ruleId];
            }
            return { ...state, ruleBusinessValues: newRuleBVs };
        }

        case ACTION_TYPES.UPDATE_RULE_DES: {
            const { ruleId, dreadedEventIds } = action.payload;
            const newRuleDEs = { ...state.ruleDreadedEvents };
            if (dreadedEventIds && dreadedEventIds.length > 0) {
                newRuleDEs[ruleId] = dreadedEventIds;
            } else { // Remove the key if the array is empty or null
                delete newRuleDEs[ruleId];
            }
            return { ...state, ruleDreadedEvents: newRuleDEs };
        }

        case ACTION_TYPES.UPDATE_RULE_GAP_COMMENT: {
            const { ruleId, comment } = action.payload;
            const newRuleComments = { ...state.ruleGapComments };
            if (comment && comment.trim().length > 0) {
                newRuleComments[ruleId] = comment;
            } else {
                delete newRuleComments[ruleId];
            }
            return { ...state, ruleGapComments: newRuleComments };
        }

        case ACTION_TYPES.SET_SEARCH_TERM:
            return { ...state, searchTerm: action.payload };

        case ACTION_TYPES.SAVE_START:
            return { ...state, isSaving: true, saveError: null, error: null };

        case ACTION_TYPES.SAVE_SUCCESS:
            return { ...state, isSaving: false, saveError: null };

        case ACTION_TYPES.SAVE_ERROR:
            return { ...state, isSaving: false, saveError: action.payload, error: action.payload };

        case ACTION_TYPES.SET_CSV_DATA:
            return { ...state, csvImportData: action.payload, importError: null };

        case ACTION_TYPES.SHOW_IMPORT_MODAL:
            return { ...state, showImportModal: true };

        case ACTION_TYPES.HIDE_IMPORT_MODAL:
            return { ...state, showImportModal: false, csvImportData: null, importError: null };

        case ACTION_TYPES.SET_IMPORT_ERROR:
            return { ...state, importError: action.payload };

        case ACTION_TYPES.UPDATE_FRAMEWORK_NAMES_CACHE:
            return { ...state, frameworkNamesCache: action.payload.frameworkNamesCache };

        case ACTION_TYPES.APPLY_MIGRATION:
            console.log(`[SecurityFramework] Applying migration to state`);
            return {
                ...state,
                selectedRules: action.payload.selectedRules,
                frameworkNamesCache: action.payload.frameworkNamesCache,
                selectedFrameworkId: action.payload.selectedFrameworkId
            };

        case ACTION_TYPES.DELETE_CUSTOM_FRAMEWORK: {
            const { frameworkId } = action.payload;
            const newFrameworkDefinitions = state.frameworkDefinitions.filter(f => (f._id || f.id) !== frameworkId && (f.stableId || f._id || f.id) !== frameworkId);
            const newSelectedRules = { ...state.selectedRules };
            const rulesToDelete = newSelectedRules[frameworkId] || [];
            delete newSelectedRules[frameworkId];
            const ruleIdsToDelete = new Set(rulesToDelete.map(r => r.id));
            const newRuleBVs = { ...state.ruleBusinessValues };
            const newRuleDEs = { ...state.ruleDreadedEvents };
            const newRuleGapComments = { ...state.ruleGapComments };
            ruleIdsToDelete.forEach(rid => { delete newRuleBVs[rid]; delete newRuleDEs[rid]; delete newRuleGapComments[rid]; });
            const newFrameworkNamesCache = { ...state.frameworkNamesCache };
            delete newFrameworkNamesCache[frameworkId];
            let newSelectedFrameworkId = state.selectedFrameworkId;
            if (newSelectedFrameworkId === frameworkId) {
                newSelectedFrameworkId = null;
                if (newFrameworkDefinitions.length > 0) {
                    const first = newFrameworkDefinitions[0];
                    newSelectedFrameworkId = first.stableId || first._id || first.id;
                }
            }
            return { ...state, frameworkDefinitions: newFrameworkDefinitions, selectedRules: newSelectedRules, ruleBusinessValues: newRuleBVs, ruleDreadedEvents: newRuleDEs, ruleGapComments: newRuleGapComments, frameworkNamesCache: newFrameworkNamesCache, selectedFrameworkId: newSelectedFrameworkId };
        }

        case ACTION_TYPES.RENAME_CUSTOM_FRAMEWORK: {
            const { frameworkId, newName } = action.payload;
            const trimmed = newName.trim();
            if (!trimmed) return state;
            const newFrameworkDefinitions = state.frameworkDefinitions.map(f => {
                const fid = f._id || f.id;
                const stable = f.stableId || fid;
                if (fid === frameworkId || stable === frameworkId) {
                    return { ...f, name: trimmed };
                }
                return f;
            });
            const newFrameworkNamesCache = { ...state.frameworkNamesCache };
            if (newFrameworkNamesCache[frameworkId]) {
                newFrameworkNamesCache[frameworkId] = trimmed;
            } else {
                const fw = newFrameworkDefinitions.find(f => (f._id || f.id) === frameworkId);
                if (fw) newFrameworkNamesCache[frameworkId] = trimmed;
            }
            return { ...state, frameworkDefinitions: newFrameworkDefinitions, frameworkNamesCache: newFrameworkNamesCache };
        }

        default:
            return state;
    }
}

// Export default for convenience
export default securityFrameworkReducer;
